/**
 * UCAS Tariff points for the qualifications most applicants hold.
 *
 * Source: UCAS, "UCAS Tariff points" tables, the 2026 and 2027 Tariff tables workbooks
 * (https://www.ucas.com/media/205211/download and https://www.ucas.com/media/229086/download,
 * linked from https://www.ucas.com/applying/before-you-apply/what-and-where-to-study/entry-requirements/ucas-tariff-points),
 * retrieved October 2026. Every value below was read straight from the workbooks' "TARIFF
 * POINTS", "TARIFF SCOTLAND" and "T LEVELS" sheets, not typed from memory.
 *
 * The two workbooks are identical for every qualification here except the IB certificates in
 * Higher and Standard Level, where the 2027 table awards points to grades the 2026 table
 * does not (HL 5 is 32 points for 2026 entry and 40 for 2027 entry). That is why the year
 * is an input.
 *
 * Tariff points are additive across qualifications. This module only adds; it does not
 * apply any university's own rules about which qualifications count.
 */

export type TariffYear = "2026" | "2027";

export const TARIFF_YEARS: readonly TariffYear[] = ["2026", "2027"];

export type TariffQualification =
  | "a-level"
  | "as-level"
  | "epq"
  | "welsh-bacc"
  | "scottish-advanced-higher"
  | "scottish-higher"
  | "btec-extended-certificate"
  | "btec-certificate"
  | "btec-diploma"
  | "btec-extended-diploma"
  | "t-level"
  | "ib-higher"
  | "ib-standard"
  | "ib-extended-essay"
  | "ib-tok";

export interface TariffGrade {
  /**
   * The grade as UCAS prints it. The two T Level passes are the exception: UCAS writes
   * "P (A*-C)" and "P (D or E)" for the core grade, which widgets label from their own
   * strings, so they carry the codes "P-high" and "P-low" here.
   */
  grade: string;
  points: number;
}

type GradeTable = readonly TariffGrade[];

const g = (grade: string, points: number): TariffGrade => ({ grade, points });

const LETTERS_A_STAR_E: GradeTable = [
  g("A*", 56),
  g("A", 48),
  g("B", 40),
  g("C", 32),
  g("D", 24),
  g("E", 16),
];

/** Grade tables that are the same for 2026 and 2027 entry. */
const COMMON: Record<
  Exclude<TariffQualification, "ib-higher" | "ib-standard">,
  GradeTable
> = {
  "a-level": LETTERS_A_STAR_E,
  "as-level": [g("A", 20), g("B", 16), g("C", 12), g("D", 10), g("E", 6)],
  epq: [g("A*", 28), g("A", 24), g("B", 20), g("C", 16), g("D", 12), g("E", 8)],
  "welsh-bacc": LETTERS_A_STAR_E,
  "scottish-advanced-higher": [g("A", 56), g("B", 48), g("C", 40), g("D", 32)],
  "scottish-higher": [g("A", 33), g("B", 27), g("C", 21), g("D", 15)],
  "btec-extended-certificate": [
    g("D*", 56),
    g("D", 48),
    g("M", 32),
    g("P", 16),
  ],
  "btec-certificate": [g("D*", 28), g("D", 24), g("M", 16), g("P", 8)],
  "btec-diploma": [
    g("D*D*", 112),
    g("D*D", 104),
    g("DD", 96),
    g("DM", 80),
    g("MM", 64),
    g("MP", 48),
    g("PP", 32),
  ],
  "btec-extended-diploma": [
    g("D*D*D*", 168),
    g("D*D*D", 160),
    g("D*DD", 152),
    g("DDD", 144),
    g("DDM", 128),
    g("DMM", 112),
    g("MMM", 96),
    g("MMP", 80),
    g("MPP", 64),
    g("PPP", 48),
  ],
  "t-level": [
    g("D*", 168),
    g("D", 144),
    g("M", 120),
    g("P-high", 96),
    g("P-low", 72),
  ],
  "ib-extended-essay": [
    g("A", 12),
    g("B", 10),
    g("C", 8),
    g("D", 6),
    g("E", 4),
  ],
  "ib-tok": [g("A", 12), g("B", 10), g("C", 8), g("D", 6), g("E", 4)],
};

const IB_HIGHER: Record<TariffYear, GradeTable> = {
  "2026": [
    g("7", 56),
    g("6", 48),
    g("5", 32),
    g("4", 24),
    g("3", 12),
    g("2", 0),
    g("1", 0),
  ],
  "2027": [
    g("7", 56),
    g("6", 48),
    g("5", 40),
    g("4", 32),
    g("3", 24),
    g("2", 16),
    g("1", 12),
  ],
};

const IB_STANDARD: Record<TariffYear, GradeTable> = {
  "2026": [
    g("7", 28),
    g("6", 24),
    g("5", 16),
    g("4", 12),
    g("3", 6),
    g("2", 0),
    g("1", 0),
  ],
  "2027": [
    g("7", 28),
    g("6", 24),
    g("5", 20),
    g("4", 16),
    g("3", 12),
    g("2", 8),
    g("1", 6),
  ],
};

export const TARIFF_QUALIFICATIONS: readonly TariffQualification[] = [
  "a-level",
  "as-level",
  "epq",
  "welsh-bacc",
  "scottish-advanced-higher",
  "scottish-higher",
  "btec-extended-certificate",
  "btec-certificate",
  "btec-diploma",
  "btec-extended-diploma",
  "t-level",
  "ib-higher",
  "ib-standard",
  "ib-extended-essay",
  "ib-tok",
];

/** The grades, best first, a qualification can earn in the given Tariff year. */
export function tariffGrades(
  qualification: TariffQualification,
  year: TariffYear
): GradeTable {
  if (qualification === "ib-higher") {
    return IB_HIGHER[year];
  }
  if (qualification === "ib-standard") {
    return IB_STANDARD[year];
  }
  return COMMON[qualification];
}

/** Points for one qualification and grade; null when the grade is not in that table. */
export function tariffPoints(
  qualification: TariffQualification,
  grade: string,
  year: TariffYear
): number | null {
  const found = tariffGrades(qualification, year).find(
    (entry) => entry.grade === grade
  );
  return found ? found.points : null;
}

export interface TariffEntry {
  qualification: TariffQualification;
  grade: string;
}

export interface TariffLine extends TariffEntry {
  points: number;
}

export interface TariffTotal {
  lines: TariffLine[];
  total: number;
}

/** Adds up the entries; entries whose grade is not in the table are skipped. */
export function sumTariff(
  entries: readonly TariffEntry[],
  year: TariffYear
): TariffTotal {
  const lines: TariffLine[] = [];
  let total = 0;
  for (const entry of entries) {
    const points = tariffPoints(entry.qualification, entry.grade, year);
    if (points !== null) {
      lines.push({ ...entry, points });
      total += points;
    }
  }
  return { lines, total };
}
